import { useState, useEffect, useMemo } from 'react';
import { isTelegramWebApp } from '@/lib/telegram-service';

/**
 * Hook per rilevare il contesto del dispositivo.
 * Usa window.matchMedia (event-driven, no polling).
 * Su Telegram WebView il breakpoint e' sempre mobile.
 */

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

export function useIsDesktop() {
  return useMediaQuery('(min-width: 1024px)');
}

export function useIsXLDesktop() {
  return useMediaQuery('(min-width: 1280px)');
}

export function useIsTablet() {
  return useMediaQuery('(min-width: 768px)');
}

export function useIsMobile() {
  return useMediaQuery('(max-width: 767px)');
}

export interface DeviceContext {
  isTelegram: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  platform: 'telegram' | 'web';
}

export function useDeviceContext(): DeviceContext {
  const isTelegram = useMemo(() => isTelegramWebApp(), []);
  const isDesktopQuery = useIsDesktop();
  const isTabletQuery = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');

  // La viewport governa il layout, anche su Telegram Desktop
  const isMobile = !isDesktopQuery && !isTabletQuery;
  const isTablet = isTabletQuery && !isDesktopQuery;
  const isDesktop = isDesktopQuery;

  return {
    isTelegram,
    isMobile,
    isTablet,
    isDesktop,
    platform: isTelegram ? 'telegram' : 'web',
  };
}

export default useDeviceContext;
